import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Store, Download, ExternalLink, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function BusinessProfile() {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => { api.get("/restaurant").then((res) => setR(res.data)); }, []);

  const set = (k) => (e) => setR({ ...r, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/restaurant", {
        name: r.name, description: r.description, cuisine: r.cuisine,
        phone: r.phone, email: r.email, address: r.address, hours: r.hours, cover_image: r.cover_image,
        logo_url: r.logo_url, brand_color: r.brand_color, accent_color: r.accent_color,
      });
      setR(data);
      toast.success("Profile saved");
    } catch (err) { toast.error(apiErr(err)); }
    setSaving(false);
  };

  if (!r) return <Loading />;

  const menuUrl = `${window.location.origin}/m/${r.slug}`;

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${r.slug}-menu-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div data-testid="profile-page">
      <PageHeader title="Business Profile & QR Menu" subtitle="Your public profile and contactless QR menu." />

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={save} className="lg:col-span-2 bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Restaurant name</Label>
              <Input data-testid="profile-name" value={r.name || ""} onChange={set("name")} /></div>
            <div className="space-y-1.5"><Label>Cuisine</Label>
              <Input value={r.cuisine || ""} onChange={set("cuisine")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label>
            <Textarea value={r.description || ""} onChange={set("description")} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Phone</Label>
              <Input value={r.phone || ""} onChange={set("phone")} /></div>
            <div className="space-y-1.5"><Label>Email</Label>
              <Input value={r.email || ""} onChange={set("email")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Address</Label>
            <Input value={r.address || ""} onChange={set("address")} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Hours</Label>
              <Input value={r.hours || ""} onChange={set("hours")} /></div>
            <div className="space-y-1.5"><Label>Cover image URL</Label>
              <Input value={r.cover_image || ""} onChange={set("cover_image")} /></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Logo URL</Label>
              <Input data-testid="profile-logo" value={r.logo_url || ""} onChange={set("logo_url")} placeholder="https://..." /></div>
            <div className="space-y-1.5"><Label>Brand color</Label>
              <div className="flex gap-2 items-center">
                <input type="color" value={r.brand_color || "#16323B"} onChange={set("brand_color")} className="h-9 w-10 rounded border border-border bg-card cursor-pointer" data-testid="profile-brand-color" />
                <Input value={r.brand_color || ""} onChange={set("brand_color")} placeholder="#16323B" /></div>
            </div>
            <div className="space-y-1.5"><Label>Accent color</Label>
              <div className="flex gap-2 items-center">
                <input type="color" value={r.accent_color || "#C7A867"} onChange={set("accent_color")} className="h-9 w-10 rounded border border-border bg-card cursor-pointer" data-testid="profile-accent-color" />
                <Input value={r.accent_color || ""} onChange={set("accent_color")} placeholder="#C7A867" /></div>
            </div>
          </div>
          <Button type="submit" data-testid="profile-save" disabled={saving} className="bg-primary hover:bg-[#B35A42] text-primary-foreground">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save profile"}
          </Button>
        </form>

        <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center text-center">
          <Store className="h-6 w-6 text-primary" />
          <h3 className="mt-2 font-medium">QR Code Menu</h3>
          <p className="text-sm text-muted-foreground mt-1">Print and place on tables. Guests scan to view your live menu.</p>
          <div ref={qrRef} className="mt-5 p-4 bg-white rounded-xl border border-border">
            <QRCodeCanvas value={menuUrl} size={180} fgColor="#1A1A1A" level="M" includeMargin />
          </div>
          <Button onClick={downloadQR} data-testid="download-qr" variant="outline" className="mt-5 w-full border-border hover:bg-muted">
            <Download className="h-4 w-4 mr-2" /> Download QR
          </Button>
          <a href={menuUrl} target="_blank" rel="noreferrer" data-testid="view-public-menu" className="mt-2 text-sm text-primary hover:underline flex items-center gap-1">
            View public menu <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
